/**
 * Step 0: Method Selection (Yandex Parsing or Manual URL Input)
 */

import { Card, Space, Tag, Alert, Typography } from 'antd'
import {
  CheckCircleOutlined,
  RocketOutlined,
  GlobalOutlined,
  FormOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons'
import type { InputMethod } from '../types'

const { Text, Title } = Typography

interface MethodSelectionStepProps {
  projectName?: string | null
  inputMethod: InputMethod | null
  onSelectMethod: (method: InputMethod) => void
}

export default function MethodSelectionStep({
  projectName,
  inputMethod,
  onSelectMethod,
}: MethodSelectionStepProps) {
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Card
        bordered={false}
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white'
        }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Title level={4} style={{ margin: 0, color: 'white' }}>
            <RocketOutlined /> Загрузка источников
          </Title>
          <Text style={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: 16 }}>
            {projectName || 'Выберите метод загрузки данных'}
          </Text>
        </Space>
      </Card>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: 24,
        marginTop: 16
      }}>
        {/* Yandex Parsing Card */}
        <Card
          hoverable
          onClick={() => onSelectMethod('yandex')}
          style={{
            cursor: 'pointer',
            border: inputMethod === 'yandex' ? '2px solid #667eea' : '1px solid #d9d9d9',
            background: inputMethod === 'yandex' ? 'linear-gradient(180deg, #f0f5ff 0%, #fff 100%)' : '#fff',
            transition: 'all 0.3s ease',
            borderRadius: 12,
          }}
          styles={{ body: { padding: 24 } }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: inputMethod === 'yandex'
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              transition: 'all 0.3s ease',
            }}>
              <GlobalOutlined style={{
                fontSize: 36,
                color: inputMethod === 'yandex' ? '#fff' : '#8c8c8c',
                transition: 'all 0.3s ease',
              }} />
            </div>

            <div>
              <Title level={4} style={{
                margin: 0,
                color: inputMethod === 'yandex' ? '#667eea' : '#262626',
                transition: 'all 0.3s ease',
              }}>
                Yandex Парсинг
              </Title>
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Автоматический сбор данных из Yandex Webmaster WordCraft API
              </Text>
            </div>

            <Space direction="vertical" size={4} style={{ width: '100%', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: 13 }}>Конкурентный анализ по запросам</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: 13 }}>Данные о кликах и позициях</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: 13 }}>Автоматический сбор доменов</Text>
              </div>
            </Space>

            {inputMethod === 'yandex' && (
              <Tag color="purple" style={{ marginTop: 8 }}>
                <CheckCircleOutlined /> Выбрано
              </Tag>
            )}
          </Space>
        </Card>

        {/* Manual URL Input Card */}
        <Card
          hoverable
          onClick={() => onSelectMethod('manual')}
          style={{
            cursor: 'pointer',
            border: inputMethod === 'manual' ? '2px solid #52c41a' : '1px solid #d9d9d9',
            background: inputMethod === 'manual' ? 'linear-gradient(180deg, #f6ffed 0%, #fff 100%)' : '#fff',
            transition: 'all 0.3s ease',
            borderRadius: 12,
          }}
          styles={{ body: { padding: 24 } }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%', textAlign: 'center' }}>
            <div style={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              background: inputMethod === 'manual'
                ? 'linear-gradient(135deg, #52c41a 0%, #73d13d 100%)'
                : 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto',
              transition: 'all 0.3s ease',
            }}>
              <FormOutlined style={{
                fontSize: 36,
                color: inputMethod === 'manual' ? '#fff' : '#8c8c8c',
                transition: 'all 0.3s ease',
              }} />
            </div>

            <div>
              <Title level={4} style={{
                margin: 0,
                color: inputMethod === 'manual' ? '#52c41a' : '#262626',
                transition: 'all 0.3s ease',
              }}>
                Ручная загрузка
              </Title>
              <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
                Вставьте список ссылок вручную для обработки
              </Text>
            </div>

            <Space direction="vertical" size={4} style={{ width: '100%', textAlign: 'left' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: 13 }}>Быстрая загрузка из буфера</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: 13 }}>Автоматическое извлечение доменов</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: 13 }}>Дедупликация ссылок</Text>
              </div>
            </Space>

            {inputMethod === 'manual' && (
              <Tag color="green" style={{ marginTop: 8 }}>
                <CheckCircleOutlined /> Выбрано
              </Tag>
            )}
          </Space>
        </Card>
      </div>

      <Alert
        message="Выберите метод загрузки"
        description={
          inputMethod === 'yandex'
            ? 'Yandex Парсинг соберёт данные о конкурентах из Yandex Webmaster по выбранным запросам и регионам.'
            : inputMethod === 'manual'
            ? 'Вставьте ссылки на сайты, и система автоматически извлечёт домены для анализа.'
            : 'Выберите один из методов выше для продолжения.'
        }
        type="info"
        showIcon
        icon={<InfoCircleOutlined />}
      />
    </Space>
  )
}
